// `common.currency.code` is an org-config value (driven by `text_config`),
// not a runtime user-toggleable language string. Reading from the i18next
// singleton directly avoids cascading a `t` parameter through 36+ call
// sites; consumers re-render on org change via React Query / Zustand,
// which picks up the new code automatically. Same escape-hatch pattern
// used in process-analysis.helpers.ts.
import i18n from 'i18next';
import type { OrganizationResponse } from '../../api/organization-client/organization-client.type';
import { isFeatureFlagEnabled } from '../feature-flags/feature-flags';
import { FeatureFlag } from '../feature-flags/feature-flags.type';

type SupportedCurrencyCode = 'USD' | 'GBP' | 'EUR';

type CurrencyInfo = {
  code: SupportedCurrencyCode;
  symbol: string;
  name: string;
};

const DEFAULT_CURRENCY_CODE: SupportedCurrencyCode = 'USD';

const DEFAULT_CURRENCY_INFO: CurrencyInfo = {
  code: DEFAULT_CURRENCY_CODE,
  symbol: '$',
  name: 'Dollars',
};

/**
 * Resolves the active currency configuration for a given organization.
 *
 * Behavior:
 * - If the {@link FeatureFlag.CustomCurrency} flag is disabled, always returns the legacy USD config.
 * - If enabled, reads `common.currency.code` from i18n/text_config and maps it to one of the supported codes.
 * - For missing or unsupported codes, safely falls back to the default USD configuration.
 *
 * This keeps admin configuration simple (only a code is provided) while ensuring the UI
 * consistently receives a normalized `{ code, symbol, name }` tuple.
 *
 * @param organization - The current organization whose currency configuration should be used.
 * @returns Normalized currency information for that organization.
 */
function getCurrency(organization: OrganizationResponse | null): CurrencyInfo {
  // If the feature flag is disabled, always behave like legacy USD.
  if (!isFeatureFlagEnabled(organization, FeatureFlag.CustomCurrency)) {
    return DEFAULT_CURRENCY_INFO;
  }

  // Read from i18n / text_config. This allows admins to configure common.currency.code.
  const rawCode = i18n.t('common.currency.code') as string | undefined;
  const upper = rawCode?.toUpperCase();

  switch (upper) {
    case 'GBP':
      return { code: 'GBP', symbol: '£', name: 'Pounds' };
    case 'EUR':
      return { code: 'EUR', symbol: '€', name: 'Euros' };
    case 'USD':
      return DEFAULT_CURRENCY_INFO;
    default:
      return DEFAULT_CURRENCY_INFO;
  }
}

export { DEFAULT_CURRENCY_CODE, getCurrency };
export type { CurrencyInfo, SupportedCurrencyCode };
